import pushNotification from "@apis/pushNotification";
import Store from "@apis/store";
import Button from "@components/button";
import Fields from "@components/fields";
import Wrapper from "@components/wrapper";
import { Form, Formik } from "formik";
import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FieldGroup, FormRow, outlineButton, UploadWrapper } from "./style";
import ButtonWrapper from "@components/wrapper/button-wrapper";
import { Helper } from "@utils/index";

interface PushNotificationFormProps {
  /** Pre-fill data when used inside a modal */
  initialData?: {
    targetAudience?: string;
    targetId?: number | string;
    title?: string;
  };
  /** Callback fired after successful submit (used to close modal) */
  onSuccess?: () => void;
}

const PushNotificationForm = ({
  initialData,
  onSuccess,
}: PushNotificationFormProps = {}) => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [stores, setStores] = useState<any[]>([]);
  const user = useMemo(() => Helper.getUser(), []);
  const [uploadKey, setUploadKey] = useState(0);

  // Prefer props over location state (modal vs page mode)
  const prefill = initialData || state;

  const [initialValues, setInitialValues] = useState({
    title: "",
    body: "",
    image: "",
    targetAudience: "all",
    targetId: "",
  });

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response: any = await Store.list({ params: { limit: 1000 } });
        setStores(response.items || []);
      } catch (error) {
        console.error("Failed to fetch stores:", error);
      }
    };

    if (user?.role === "admin") {
      fetchStores();
    }
  }, [user]);

  useEffect(() => {
    setInitialValues({
      title: prefill?.title || "",
      body: prefill?.description || "",
      image: prefill?.image || "",
      targetAudience:
        prefill?.targetAudience ||
        (user?.role === "store" ? "customer" : "all"),
      targetId: prefill?.targetId || (user?.role === "store" ? user?.id : ""),
    });
  }, [prefill, user]);

  const handleSubmit = async (values: any, { resetForm }: any) => {
    try {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("body", values.body);
      formData.append("targetAudience", values.targetAudience);
      if (values.targetId) {
        formData.append("targetId", String(values.targetId));
      }

      // Handle image - append raw file if available
      if (values.image?.rawFile) {
        formData.append("image", values.image.rawFile);
      }

      if (prefill?.id) {
        await pushNotification.update({
          state: "pushNotification",
          data: formData,
          id: prefill.id,
        });
      } else {
        await pushNotification.create({
          state: "pushNotification",
          data: formData,
        });
      }

      resetForm();
      setUploadKey((k) => k + 1);

      // Modal mode: call onSuccess callback
      if (onSuccess) {
        onSuccess();
      } else if (prefill?.id) {
        navigate(-1);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const audienceOptions = useMemo(() => {
    if (user?.role === "store") {
      return [{ label: "My Customers", value: "customer" }];
    }
    return [
      { label: "All Users", value: "all" },
      { label: "Specific Store", value: "store" },
      { label: "Specific Store Customers", value: "customer" },
      { label: "All Delivery Personnel", value: "delivery" },
      { label: "Specific Customer", value: "specific_customer" },
      { label: "Specific Delivery Person", value: "specific_delivery" },
    ];
  }, [user]);

  return (
    <Wrapper>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values }) => (
          <Form>
            <FormRow>
              <FieldGroup>
                <Fields.Input
                  name="title"
                  label="Title"
                  placeholder="Type here"
                  styleInput="width:100%"
                />

                <FormRow style={{ padding: 0, gap: "20px" }}>
                  <Fields.DropDown
                    name="targetAudience"
                    label="Target Audience"
                    placeholder="Select Audience"
                    options={audienceOptions}
                    valueAsKey="value"
                    titleAsKey="label"
                    disabled={user?.role === "store"}
                    styleWrapper="flex: 1"
                  />

                  {user?.role === "admin" &&
                    (values.targetAudience === "store" ||
                      values.targetAudience === "customer") && (
                      <Fields.DropDown
                        name="targetId"
                        label="Select Store"
                        placeholder="Select Store"
                        options={stores}
                        valueAsKey="id"
                        titleAsKey="storeName"
                        hasSearch
                        styleWrapper="flex: 1"
                      />
                    )}

                  {user?.role === "admin" &&
                    (values.targetAudience === "specific_customer" ||
                      values.targetAudience === "specific_delivery") && (
                      <Fields.Input
                        name="targetId"
                        label="Selected User ID"
                        placeholder="User ID"
                        disabled
                        styleWrapper="flex: 1"
                      />
                    )}
                </FormRow>

                <Fields.TextArea
                  name="body"
                  label="Description"
                  placeholder="Type here"
                  styleInput="max-width:100%;height:120px;resize:none;"
                />
              </FieldGroup>

              <UploadWrapper>
                <Fields.Upload
                  key={uploadKey}
                  name="image"
                  variant="banner"
                  useRawFile
                />
              </UploadWrapper>
            </FormRow>
            <br />
            <ButtonWrapper>
              <Button
                type="reset"
                title="Reset"
                height={45}
                width={100}
                styleButton={outlineButton}
              />

              {!initialData && prefill && (
                <Button
                  title="Cancel"
                  height={45}
                  width={100}
                  onClick={() => navigate(-1)}
                  styleButton={`
                    background-color: #dc3545;
                    color: white;
                    border: none;
                  `}
                />
              )}

              <Button
                title={prefill?.id ? "Update" : "Send"}
                height={45}
                width={100}
                type="submit"
              />
            </ButtonWrapper>
          </Form>
        )}
      </Formik>
    </Wrapper>
  );
};

export default PushNotificationForm;
